import React, {
  createContext, useContext, useEffect, useState, useCallback, useRef,
} from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import api from '../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

type LocalServerStatus = 'detecting' | 'available' | 'unavailable';

interface UpdateInfo {
  version: string;
  filename: string;
}

interface LocalServerContextValue {
  status: LocalServerStatus;
  localServerUrl: string | null;
  /** True when the local server is reachable */
  isAvailable: boolean;
  /** True once the local server has been seen available at least once this session —
   * lets a status badge stay hidden for restaurants that simply never deployed one,
   * instead of permanently showing "unavailable" (the probe always tries localhost:3001). */
  hasEverConnected: boolean;
  /** True only once the local server has stayed unreachable for several consecutive
   * probes — a single missed probe is treated as a network blip, not an outage. Only
   * meaningful for restaurants that have a local server at all (hasEverConnected). */
  isDurablyDown: boolean;
  /** Version reported by the currently connected local server's /health, if any. */
  version: string | null;
  /** Set once a cloud version check (throttled to 1x/day) finds a newer build than `version`. */
  updateAvailable: UpdateInfo | null;
  /** Resolved API base URL: local server if available, else cloud */
  resolvedApiUrl: (cloudUrl: string) => string;
  /** Force a new probe (e.g. after network change) */
  recheck: () => void;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const STORAGE_KEY = 'local_server_url';
const UPDATE_CHECK_STORAGE_KEY = 'local_server_update_checked_at';
const UPDATE_CHECK_INTERVAL = 24 * 60 * 60 * 1000; // 1x/day — a version check doesn't need 30s freshness
const PROBE_TIMEOUT = 2_000;
const PROBE_INTERVAL = 30_000;
// ponytail: consecutive-failure count as a stand-in for real connection-quality
// tracking — a single failed probe (Wi-Fi blip) doesn't count as an outage, only
// a sustained one (~60s at the current 30s interval) does. Upgrade to a proper
// signal (RTT/jitter) if 2 consecutive misses proves too trigger-happy in practice.
const DURABLE_DOWN_STREAK = 2;

// Routes intercepted by local server when available.
// Catalog reads: products, categories, spaces, payment-methods, taxes, garnishes.
// Taxes/garnishes: full CRUD now (local-server/src/routes/taxes.routes.ts,
// garnishes.routes.ts) — checked every dashboard call under these prefixes
// against the routes actually implemented (GET/POST/PUT /taxes/:id,
// GET/POST/PATCH/DELETE /garnishes/{groups,options}[/:id]) before adding, same
// diligence as the POS-terminals collision check below — no gaps found.
// Printing: only sign-fiscal and drawer-open handled locally; /printing/order/*/kitchen
// and /receipt must fall through to cloud (matched via '/sign-fiscal' and '/printing/drawer',
// not the broader '/printing/order' prefix, which used to also catch kitchen/receipt and
// silently redirect them locally). Drawer-open has no order/sync dependency — it's a pure
// hardware action worth keeping available when the internet is down.
// Fiscal: manifest + sync proxy to S-MDF on LAN (avoids CORS).
// Employees: PIN/badge auth handled locally; CRUD routes fall through to cloud proxy.
// Auth forgot-password: PIN-verified self-service reset, fully local (no email/SMTP
// capability here by design) — must resolve locally even when cloud is unreachable,
// which is precisely when a manager is most likely to need it. Scoped to
// '/auth/forgot-password' only (not the broader '/auth'), since /auth/login and
// /auth/me are only served locally in VITE_LOCAL_MODE (same-origin baseURL), not
// via this proxy-style route matching.
// Cash sessions: fully local SQLite (open/close/movements/current/active) — never proxied to cloud.
// Inventory stock: items, movements, valuation, replenishment — fully local SQLite
// (see local-server/src/routes/inventory.routes.ts). Scoped to '/inventory/stock' (not
// the broader '/inventory' or '/warehouses'), which also cover suppliers, procurement,
// inventory counts, technical sheets, transfers and warehouse CRUD — none of those are
// implemented on local-server yet, so they must keep falling through to cloud.
// Analytics: sales/products/payments/categories/servers/hours/drilldown/cancelled-items/
// kpis/time-analysis/menu-engineering/inventory-value — computed from the local SQLite
// mirror (see local-server/src/routes/analytics.routes.ts). Food-cost/variance/forecast/
// abc/inflation-alerts are NOT covered (need recipe costing data not mirrored locally)
// and keep falling through to cloud.
// Reviews: consult-only (QR customer feedback always submitted straight to the cloud,
// never created here) — GET /reviews reads the local SQLite mirror once synced.
// Permissions: access-rights screen — restaurant role overrides edited locally and
// pushed to the cloud (see local-server/src/routes/permissions.routes.ts).
// POS session routes: GET /pos/:id/sessions and GET /pos/:id/session are now
// handled by local-server (see local-server/src/routes/pos.routes.ts).
// We match them via POS_SESSION_ROUTE_REGEX below (precise regex) rather than
// adding '/pos' to LOCAL_ROUTES (which would incorrectly swallow all /pos/* routes).
const LOCAL_ROUTES = [
  '/products',
  '/categories',
  '/spaces',
  '/payment-methods',
  '/taxes',
  '/garnishes',
  '/sign-fiscal',
  '/printing/drawer',
  '/fiscal',
  '/employees',
  '/auth/forgot-password',
  '/cash-sessions',
  '/inventory/stock',
  '/analytics',
  '/credit-notes',
  '/debts',
  '/suppliers',
  '/reviews',
  '/permissions',
];

// Additional POS session endpoints served by the local-server (matched via regex)
// Matches: /pos/:id/sessions, /pos/:id/session, /pos/sessions/:id/summary
const POS_SESSION_ROUTE_REGEX = /\/pos\/(?:sessions\/[^/?]+\/summary|[^/?]+\/sessions?(?:[/?]|$))/;

// ─── Module-level snapshot ──────────────────────────────────────────────────
// Mirrors the React state outside of React so plain modules (e.g. api.ts's
// axios interceptor) can read the current local server URL synchronously.

let currentLocalServerUrl: string | null = null;

/** Returns the currently detected local server URL, or null if unavailable. */
export function getLocalServerSnapshot(): string | null {
  return currentLocalServerUrl;
}

/** True if `path` is one of the catalog routes served by the local server. */
export function isLocalCatalogRoute(path: string): boolean {
  return LOCAL_ROUTES.some((prefix) => path.includes(prefix))
    || POS_SESSION_ROUTE_REGEX.test(path);
}

// ─── Context ──────────────────────────────────────────────────────────────────

const LocalServerContext = createContext<LocalServerContextValue>({
  status: 'detecting',
  localServerUrl: null,
  isAvailable: false,
  hasEverConnected: false,
  isDurablyDown: false,
  version: null,
  updateAvailable: null,
  resolvedApiUrl: (url) => url,
  recheck: () => {},
});

export function useLocalServer() {
  return useContext(LocalServerContext);
}

// ─── Helper: detect local server URL ─────────────────────────────────────────

// RFC1918 private ranges + loopback — used to decide whether the page's own
// hostname is worth trying as a local-server candidate (see below).
function isPrivateHost(hostname: string): boolean {
  return hostname === 'localhost'
    || hostname === '127.0.0.1'
    || /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[0-1])\.)/.test(hostname);
}

// The restaurant slug implied by the current subdomain (e.g. "restaurant-le-demo"
// from restaurant-le-demo.restaurant.koolofood.com), when this is a multi-tenant
// subdomain deployment — same heuristic as POSContext's own hostname parsing.
// Null when it can't be determined (admin.<domain>, a bare LAN IP, a dedicated
// single-restaurant self-hosted install by IP, etc.) — in that case there's no
// signal to verify against, so detection falls back to the old trust-any-probe
// behavior (safe: those setups only ever have one restaurant in play at all).
function expectedSlugFromHostname(): string | null {
  const { hostname } = window.location;
  const isIpAddress = /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);
  if (isIpAddress) return null;
  const parts = hostname.split('.');
  if (parts.length <= 2 && !(hostname.includes('localhost') && parts.length > 1)) return null;
  const first = parts[0];
  if (['www', 'app', 'admin'].includes(first)) return null;
  return first;
}

function getCandidateUrls(): string[] {
  // 1. Explicitly configured (restaurant settings page) — e.g. a multi-terminal
  // LAN setup where the local-server runs on a different machine than the one
  // probing it; no way to guess that IP, so it must be entered once and stored.
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) return [stored.replace(/\/$/, '')];

  // 2. Build-time env variable
  const envUrl = import.meta.env.VITE_LOCAL_SERVER_URL as string | undefined;
  if (envUrl) return [envUrl.replace(/\/$/, '')];

  // 3. No explicit config: the common case is the local-server running on the
  // very machine the browser is on, reachable at localhost:3001 — or, when the
  // dashboard itself was opened via a LAN address (self-hosted install visited
  // by IP), that same host. Never rely on the koolfood-local.local mDNS name:
  // it doesn't resolve on plenty of real networks (no Bonjour/mDNS support)
  // and silently eats the whole probe timeout every cycle before falling
  // through to the (offline) cloud — the bug this replaces.
  const { hostname } = window.location;
  const candidates = ['http://localhost:3001'];
  if (hostname !== 'localhost' && isPrivateHost(hostname)) {
    candidates.unshift(`http://${hostname}:3001`);
  }
  return candidates;
}

async function probeLocalServer(url: string): Promise<{ ok: boolean; version: string | null; slug: string | null; os: 'win10' | 'win7' }> {
  try {
    const res = await axios.get(`${url}/health`, { timeout: PROBE_TIMEOUT });
    return {
      ok: res.status === 200 && res.data?.status === 'ok',
      version: res.data?.version ?? null,
      slug: res.data?.slug ?? null,
      os: res.data?.os === 'win7' ? 'win7' : 'win10',
    };
  } catch {
    return { ok: false, version: null, slug: null, os: 'win10' };
  }
}

/** true if `a` (e.g. cloud's latest build) is strictly newer than `b` (e.g. the connected instance) */
function isNewerVersion(a: string, b: string): boolean {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const x = pa[i] ?? 0;
    const y = pb[i] ?? 0;
    if (x !== y) return x > y;
  }
  return false;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function LocalServerProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<LocalServerStatus>('detecting');
  const [localServerUrl, setLocalServerUrl] = useState<string | null>(null);
  const [hasEverConnected, setHasEverConnected] = useState(false);
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);
  const [version, setVersion] = useState<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState<UpdateInfo | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks the previous resolved status (not 'detecting') to fire a toast only on a
  // real transition — e.g. a local server that crashes mid-shift — not on every probe.
  const prevResolvedRef = useRef<'available' | 'unavailable' | null>(null);

  const detect = useCallback(async () => {
    let next: 'available' | 'unavailable' = 'unavailable';
    let detectedVersion: string | null = null;
    let detectedOs: 'win10' | 'win7' = 'win10';
    const expectedSlug = expectedSlugFromHostname();
    for (const candidate of getCandidateUrls()) {
      const probe = await probeLocalServer(candidate);
      // Reject a local-server paired to a different restaurant — otherwise a stray
      // instance reachable on this machine/network (a different test restaurant,
      // a stale stored URL from a prior pairing) silently hijacks auth/catalog/etc.
      // for whichever restaurant is actually being managed right now. A probe with
      // no slug at all (unpaired/fresh install, `configured: false`) is untrusted
      // too, not waved through — it used to slip past `probe.slug &&`, so literally
      // any HTTP server answering on :3001 with a 200 + {status:'ok'} body (a stray
      // dev tool, a leftover container from another project) would get accepted as
      // "this restaurant's local server" and start intercepting its /employees,
      // /cash-sessions, /auth/forgot-password, etc. instead of the cloud.
      if (expectedSlug && probe.slug !== expectedSlug) continue;
      if (probe.ok) {
        setLocalServerUrl(candidate);
        currentLocalServerUrl = candidate;
        detectedVersion = probe.version;
        detectedOs = probe.os;
        next = 'available';
        break;
      }
    }
    if (next === 'unavailable') {
      setLocalServerUrl(null);
      currentLocalServerUrl = null;
      setConsecutiveFailures((n) => n + 1);
    } else {
      setConsecutiveFailures(0);
    }
    setVersion(detectedVersion);
    setStatus(next);

    // Cloud "is there a newer build?" check — throttled to 1x/day, independent of the
    // 30s LAN probe cadence above (a version check has no reason to be that eager).
    if (next === 'available' && detectedVersion) {
      const lastChecked = Number(localStorage.getItem(UPDATE_CHECK_STORAGE_KEY) || 0);
      if (Date.now() - lastChecked > UPDATE_CHECK_INTERVAL) {
        try {
          // Must match the connected instance's OS variant — a win7 install can't
          // run a win10 build (see local-sync.service.ts's getLatestBuild(os)).
          const res = await api.get(`/local-sync/latest-build?os=${detectedOs}`);
          const latest = res.data?.data as UpdateInfo | null;
          setUpdateAvailable(
            latest && isNewerVersion(latest.version, detectedVersion) ? latest : null,
          );
        } catch {
          // Cloud unreachable or no build published — leave the previous result in
          // place, try again on the next throttle window rather than clearing it.
        } finally {
          localStorage.setItem(UPDATE_CHECK_STORAGE_KEY, String(Date.now()));
        }
      }
    }

    const prev = prevResolvedRef.current;
    if (prev === 'available' && next === 'unavailable') {
      toast.error('Serveur local déconnecté — retour en mode cloud', { id: 'local-server-status' });
    } else if (prev === 'unavailable' && next === 'available') {
      toast.success('Serveur local reconnecté', { id: 'local-server-status' });
    }
    if (next === 'available') setHasEverConnected(true);
    prevResolvedRef.current = next;
  }, []);

  useEffect(() => {
    detect();
    timerRef.current = setInterval(detect, PROBE_INTERVAL);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [detect]);

  /**
   * Returns the base URL to use for a given cloud API path.
   * Catalog routes (products, categories, spaces, payment-methods) → local server.
   * All others → cloud (passed through unchanged).
   */
  const resolvedApiUrl = useCallback(
    (cloudPath: string): string => {
      if (status !== 'available' || !localServerUrl) return cloudPath;
      const isLocalRoute = LOCAL_ROUTES.some((prefix) => cloudPath.includes(prefix));
      if (!isLocalRoute) return cloudPath;

      // Replace the base cloud URL part with local server URL
      // e.g. https://api.koolfood.io/api/v1/products → http://localhost:3001/api/v1/products
      try {
        const url = new URL(cloudPath);
        return `${localServerUrl}${url.pathname}${url.search}`;
      } catch {
        return cloudPath;
      }
    },
    [status, localServerUrl],
  );

  const value: LocalServerContextValue = {
    status,
    localServerUrl,
    isAvailable: status === 'available',
    hasEverConnected,
    isDurablyDown: hasEverConnected && status === 'unavailable' && consecutiveFailures >= DURABLE_DOWN_STREAK,
    version,
    updateAvailable,
    resolvedApiUrl,
    recheck: detect,
  };

  return (
    <LocalServerContext.Provider value={value}>
      {children}
    </LocalServerContext.Provider>
  );
}

// ─── Hook for components that need a local-aware axios instance ───────────────

/**
 * Returns a factory function that creates an axios instance pointing to
 * the local server for catalog routes, and cloud for everything else.
 *
 * Usage:
 *   const { makeLocalAwareApi } = useLocalAwareApi();
 *   const api = makeLocalAwareApi(cloudBaseUrl);
 */
export function useLocalAwareApi() {
  const { isAvailable, localServerUrl } = useLocalServer();

  const getBaseUrl = useCallback(
    (routePath: string, cloudBaseUrl: string): string => {
      if (!isAvailable || !localServerUrl) return cloudBaseUrl;
      const isLocalRoute = LOCAL_ROUTES.some((prefix) => routePath.startsWith(prefix));
      return isLocalRoute ? localServerUrl : cloudBaseUrl;
    },
    [isAvailable, localServerUrl],
  );

  return { getBaseUrl, isAvailable, localServerUrl };
}
